import { useState } from "react";
import { toast } from "react-toastify";
import Button from "components/atoms/buttons/Button";
import Loading from "components/atoms/Loading";
import { useRouter } from "components/hooks";
import { getMagnetPageLink } from "utils/helpers";
import { formatToCommaSeparatedString } from "utils/HelperFunctions";
import FeatureCard from "components/atoms/FeatureCard";
import { ROUTES, TOAST_CONFIG } from "../../../../constants";
import { MagnetOverviewCard } from "./Components";
import ConfirmPublishMagnetModal from "./Components/ConfirmPublishMagnetModal";

interface IProps {
  magnet: BrandMagnetDetails | null;
  publishMagnet: (
    magnetId: string,
    handleSuccess: (data: any) => any,
    handleError: (errorMessage: string, errorData: any) => any,
  ) => any;
  reloadMagnetDetails: () => any;
  brandId?: string | null;
}

function OverviewComponent({ publishMagnet, reloadMagnetDetails, magnet, brandId }: IProps) {
  const router = useRouter();
  const { id: magnetId } = magnet as BrandMagnetDetails;

  const [isPublishConfirmModalVisible, setIsPublishConfirmModalVisible] = useState(false);
  const [isPublishingMagnet, setIsPublishingMagnet] = useState(false);

  if (!magnet) {
    return null;
  }
  const triggerPublishMagnetModal = () => {
    setIsPublishConfirmModalVisible(true);
  };

  const onClosePublishMagnetModal = () => {
    setIsPublishConfirmModalVisible(false);
  };

  const handleEditMagnetButtonClick = () => {
    router.go(`${ROUTES.EDIT_MAGNET}/${magnetId}`);
  };

  const handleViewMagnetPageButtonClick = () => {
    router.go(getMagnetPageLink(brandId || "", magnetId));
  };

  const handlePublishMagnet = () => {
    setIsPublishingMagnet(true);
    publishMagnet(
      magnetId,
      () => {
        toast.info("Publishing Magnet", TOAST_CONFIG);
        setIsPublishingMagnet(false);
        reloadMagnetDetails();
        onClosePublishMagnetModal();
      },
      () => {
        toast.error("Failed to publish Magnet", TOAST_CONFIG);
        setIsPublishingMagnet(false);
        onClosePublishMagnetModal();
      },
    );
  };

  return (
    <>
      {isPublishingMagnet && <Loading />}
      <ConfirmPublishMagnetModal
        open={isPublishConfirmModalVisible}
        onClose={onClosePublishMagnetModal}
        handlePublish={handlePublishMagnet}
      />
      <div className="mt-10 flex w-auto justify-end xl:-mt-10 2xl:-mt-12">
        <div className="flex  justify-between gap-3.5">
          <Button
            variant="whiteOutline"
            additionalClasses="xl:text-xs 2xl:text-sm xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
            text="VIEW MAGNET PAGE"
            onClick={handleViewMagnetPageButtonClick}
          />
          <Button
            variant="rustPinkSolid"
            additionalClasses="xl:text-xs 2xl:text-sm xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
            text="EDIT MAGNET"
            onClick={handleEditMagnetButtonClick}
          />
        </div>
      </div>
      <div className="mb-10 mt-6 w-full 2xl:mt-8">
        <MagnetOverviewCard
          magnetStatus={magnet.status}
          handlePublishMagnet={triggerPublishMagnetModal}
          name={magnet.name}
          description={magnet.description}
          imageSrc={magnet.image_url}
          totalSupply={formatToCommaSeparatedString(magnet.max_supply, "decimal")}
          availableSupply={formatToCommaSeparatedString(magnet.available_supply, "decimal")}
          total_claim_form_responses={magnet.total_claim_form_responses}
          magnet_id={magnet.id}
        />
        <div className="mb-6 mt-8 w-full border-b-2 border-dashed 2xl:mb-8 2xl:mt-10" />
        <p className="font-sora text-xs font-medium text-white underline 2xl:text-sm">MAGNET DETAILS</p>
        <div className="mt-4 flex 2xl:mt-6">
          <div className="w-1/3">
            <div className="font-md font-sora text-xs font-medium text-white opacity-90 2xl:text-sm">
              Magnetized Because
            </div>
            <div className="mt-3 font-satoshi text-xs font-450 leading-7 text-white opacity-60 2xl:mt-5 2xl:text-sm">
              {magnet.magnetized_because}
            </div>
          </div>
          <div className="ml-14 w-full 2xl:ml-16">
            <p className="font-md font-sora text-xs font-medium text-white opacity-90 2xl:text-sm">Magnet Benefits</p>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-3 2xl:mt-3 2xl:gap-x-6 2xl:gap-y-4">
              {magnet.benefits?.map((benefit) => (
                <div key={`mag_benefit${benefit.id}`} className="rounded-2xl border-opacity-10 hover:overflow-visible">
                  <FeatureCard
                    imageHash={benefit.image_hash}
                    featureImage={benefit.image_url}
                    title={benefit.title}
                    description={benefit.description}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OverviewComponent;
